/**
 * EN / ES translations for the AI agents homepage, the audit intake modal,
 * the agent simulator, and the /vs-* comparison pages.
 * Imported and merged into the main translations object.
 */
export const aiAgentsTranslations = {
  en: {
    aiAudit: {
      cta: "Book Your AI Operations Audit ($750)",
      modalTitle: "Book your AI Operations Audit",
      modalSubtitle:
        "Tell us a little about your business. We'll reply within one business day to schedule your audit.",
      nameLabel: "Your name",
      businessLabel: "Business name",
      emailLabel: "Email",
      phoneLabel: "Phone",
      tradeLabel: "Industry",
      tradeOptions: [
        "Construction & Manufacturing",
        "Legal Services",
        "Medical & Healthcare",
        "Retail & E-Commerce",
        "Real Estate & Property Management",
        "Local Services & Beauty",
        "Other",
      ],
      teamSizeLabel: "Team size",
      teamSizeOptions: ["1–9 people", "10–20 people", "21–50 people", "50+ people"],
      toolsLabel: "Tools you use today",
      toolsPlaceholder: "e.g. QuickBooks, HubSpot, Google Drive",
      headacheLabel: "Biggest admin headache",
      headachePlaceholder: "e.g. Pulling weekly numbers takes my office manager all of Monday.",
      selectPlaceholder: "Select…",
      submit: "Request a call",
      cancel: "Cancel",
      close: "Close",
      priceNote: "The audit is $750 and credited in full toward your build if you proceed. No commitment to further work.",
      callCta: "Book a free 15-min call",
      callNote: "The call is free. If we're a fit, the audit is $750, credited toward your build.",
      sentTitle: "Thanks, we got your request",
      sentText:
        "We'll reply within one business day to schedule your audit.",
      callSentText: "We'll reply within one business day to schedule your free call.",
      done: "Done",
    },
    aiSimulator: {
      windowTitle: "Kranbos Agent Preview",
      suggestionsLabel: "Try a sample question:",
      placeholder: "Ask about past quotes, job specs, or invoices…",
      send: "Send",
      typing: "Checking your files and tools…",
      userLabel: "You",
      agentLabel: "Kranbos Agent",
      sourceLabel: "Source",
      exampleNote: "Sample business data for illustration.",
      fallback:
        "This preview only answers the sample questions above. In your business, the agent answers from your own documents, price sheets, and software, and shows where each answer came from.",
      scenarios: [
        {
          id: "warranty",
          chip: "Warranty lookup",
          question:
            "What's the warranty on commercial HVAC installs in our 2024 service agreement?",
          answer: "Per section 4.2 of your 2024 Commercial Service Agreement:",
          bullets: [
            "Parts & labor: 2-year full coverage",
            "Compressor: 5-year limited warranty",
            "Requires the annual maintenance visit on file",
          ],
          followUp: "",
          source: "Google Drive › Contracts › 2024 Commercial Service Agreement.pdf",
          keywords: ["warranty", "coverage", "guarantee"],
        },
        {
          id: "unbilled",
          chip: "Unbilled jobs",
          question: "Which jobs finished last week still haven't been invoiced?",
          answer: "3 completed jobs from last week have no invoice in QuickBooks yet:",
          bullets: [
            "#4471 Water heater replacement (Rivera): $2,380",
            "#4475 Panel upgrade (Oak St. Dental): $4,150",
            "#4482 Drain repair (Chen): $640",
          ],
          followUp: "Total unbilled: $7,170. Want me to draft the invoices?",
          source: "Jobber (completed jobs) + QuickBooks (invoices)",
          keywords: ["invoice", "invoiced", "unbilled", "billing", "bill"],
        },
        {
          id: "report",
          chip: "Weekly owner report",
          question: "Give me this week's summary for the Monday meeting.",
          answer: "Here's your week at a glance (Mon–Fri):",
          bullets: [
            "42 jobs completed, 5 callbacks (down 12% vs. last week)",
            "Invoiced: $61,300 · Outstanding over 30 days: $8,900",
            "Top crew by completed jobs: Team B (14)",
          ],
          followUp: "The full report lands in your inbox every Monday at 7:00 AM.",
          source: "ServiceTitan + QuickBooks",
          keywords: ["report", "summary", "week", "weekly", "numbers", "meeting"],
        },
        {
          id: "pricing",
          chip: "Price sheet",
          question: "What do we charge for a 50-gallon water heater swap with haul-away?",
          answer: "From your 2024 Residential Price Sheet:",
          bullets: [
            "50-gal gas water heater, installed: $1,850",
            "Haul-away & disposal: $95",
            "Permit (if required): billed at cost",
          ],
          followUp: "Maintenance plan members get 10% off labor.",
          source: "Google Drive › Pricing › 2024 Residential Price Sheet.xlsx",
          keywords: ["price", "pricing", "charge", "cost", "quote", "heater"],
        },
      ],
    },
    aiHome: {
      hero: {
        beforeTitle: "Manual work & chaos",
        beforeAlt:
          "A stressed business owner at a cluttered desk with messy spreadsheets, scattered papers, and sticky notes",
        afterTitle: "Automated workflow",
        afterAlt:
          "The same business owner relaxed with a coffee, looking at a clean Kranbos dashboard showing quotes generated and automated tasks complete",
        title: "For businesses that quote, report and answer the same questions all day",
        titleAccent: "the same questions all day",
        badge: "No more copy-paste quotes and reports",
        subtitle:
          "Custom AI connected to your price sheets, files and software. It drafts quotes, reports and answers. Your team reviews and sends.",
      },
      industries: {
        title: "Built around the work your team repeats every day.",
        titleAccent: "the work your team repeats",
        subtitle: "Three kinds of work we automate, with examples from real client projects.",
        groups: [
          {
            id: "quote-heavy",
            icon: "request_quote",
            title: "Quote-heavy businesses",
            who: "Trades, construction, manufacturing, installers, landscaping, event and print companies.",
            examples: ["PDF quotes from your price sheets", "Intake form to quote", "CRM-synced estimates"],
          },
          {
            id: "document-heavy",
            icon: "description",
            title: "Document-heavy businesses",
            who: "Law firms, property management, insurance agencies, accounting and other practices.",
            examples: [
              "Private search across your contracts and SOPs",
              "Answers from your own files, with source links",
            ],
          },
          {
            id: "reporting-heavy",
            icon: "monitoring",
            title: "Reporting-heavy businesses",
            who: "Any team rebuilding the same monthly report from spreadsheets.",
            examples: ["Automated executive reports from your project data", "Dashboards that refresh on a schedule"],
          },
        ],
        tilesTitle: "Industries we can build for",
        catchAll:
          "Don't see your industry? If your team works from price sheets, contracts or spreadsheets, we can probably help.",
        catchAllCta: "Book a free 15-min call.",
        items: [
          { icon: "construction", label: "Construction, Trades & Manufacturing", path: "/industries/construction-manufacturing/" },
          { icon: "apartment", label: "Real Estate & Property Management", path: "/industries/real-estate/" },
          { icon: "storefront", label: "Retail & E-Commerce", path: "/industries/retail-ecommerce/" },
          { icon: "spa", label: "Local Services & Beauty", path: "/industries/local-services/" },
          { icon: "gavel", label: "Law Firms & Legal Services", path: "/industries/law-firms/" },
          { icon: "medical_services", label: "Medical & Healthcare Practices", path: "/industries/medical-practices/" },
        ],
      },
      value: {
        title: "Generic AI gives you generic answers. Kranbos builds AI that knows your business.",
        eyebrow: "Why Kranbos",
        titleAccent: "knows your business.",
        subtitle:
          "ChatGPT and Gemini are great for drafting and brainstorming. Keep using them for that.\nKranbos handles repeatable, data-dependent work that chat tools aren't built for.",
        featureHeader: "Feature",
        genericHeader: "Chat AI tools (like ChatGPT or Gemini)",
        kranbosHeader: "Kranbos",
          rows: [
            {
              feature: "Data source",
              generic: "Someone pastes or uploads files each time",
              kranbos: "Connected to your price sheets, CRM, and documents, and kept in sync",
            },
            {
              feature: "Output",
              generic: "Chat text you copy and reformat",
              kranbos: "Finished PDFs, spreadsheets, and CRM records",
            },
            {
              feature: "Workflow",
              generic: "Someone has to start each task",
              kranbos: "Runs on its own from a form or CRM event",
            },
            {
              feature: "Control",
              generic: "Each employee decides what to paste in",
              kranbos: "One governed place with role-based access and activity logs",
            },
          ],
        privacyNote:
          "Instead of files copied into each employee's chat history, Kranbos keeps your data in one place with role-based access and logs.",
      },
      howItWorks: {
        title: "From first call to working AI in 4 steps",
        eyebrow: "How it works",
        titleAccent: "in 4 steps",
        stepLabel: "Step",
        steps: [
          {
            icon: "call",
            title: "Free 15-min call",
            text: "Tell us what eats up your team's time. We'll tell you honestly whether custom AI is a fit.",
          },
          {
            icon: "fact_check",
            title: "AI Operations Audit",
            text: "We map your workflows and files, then send a fixed-price proposal. $750, credited toward your build.",
          },
          {
            icon: "build",
            title: "Build and test",
            text: "We connect your tools and test on your real files. Quotes and reports can require approval before sending.",
          },
          {
            icon: "groups",
            title: "Train and hand off",
            text: "We train your team in plain English. Ongoing support is optional.",
          },
        ],
      },
      security: {
        title: "Your data stays private and access-controlled",
        eyebrow: "Security & privacy",
        titleAccent: "access-controlled",
        trustLink: "How we handle your data",
        items: [
          {
            icon: "block",
            title: "Not used to train public models",
            text: "Your files and price sheets are not used to train public AI models.",
          },
          {
            icon: "cloud_lock",
            title: "Isolated private cloud",
            text: "Your data lives in an isolated, encrypted environment dedicated to your company.",
          },
          {
            icon: "admin_panel_settings",
            title: "Role-based access",
            text: "Access is role-based, so you decide who on your team can see which data.",
          },
        ],
      },
      faq: {
        title: "Frequently asked questions",
        eyebrow: "FAQ",
        titleAccent: "questions",
        items: [
          {
            q: "Will my team need to learn complicated technical tools?",
            a: "No. If your team can fill out a web form, they can use Kranbos. We handle the technical setup.",
          },
          {
            q: "What if our spreadsheets or files are messy?",
            a: "That’s normal. During your audit we clean and structure your data, then test the AI on your own past quotes or reports and show you the match rate before go-live. Anything it isn't confident about is flagged for review.",
          },
          {
            q: "Can we check quotes and reports before they go out?",
            a: "Yes. Quotes and reports can require approval before sending until you trust the results.",
          },
          {
            q: "Do we have to replace the software we already use?",
            a: "No. Kranbos connects to the tools you already use, like HubSpot, Jobber, Excel and Google Workspace.",
          },
          {
            q: "How long does the setup process take?",
            a: "Smaller builds are typically built, tested and handed over within 2 to 3 weeks. Larger, multi-workflow projects are scheduled in your proposal.",
          },
          {
            q: "How much does it cost?",
            a: "The first 15-minute call is free. The audit is a fixed $750, credited toward your build. Builds are quoted after the audit, typically $5,000 to $15,000, with smaller projects from around $3,500. Ongoing support is optional and starts at $300/month.",
          },
          {
            q: "Do I need ongoing support?",
            a: "Not always. Some businesses are fine on their own after handoff. We'll recommend whether support makes sense once we see how your workflows and data change over time.",
          },
        ],
      },
      engagement: {
        title: "Clear pricing, scoped to your business",
        eyebrow: "Pricing",
        titleAccent: "scoped to your business",
        subtitle:
          "Every business runs differently, so builds are quoted after your audit. Start with a free call, then a fixed-price audit and proposal, then decide.",
        featured: "Core engagement",
        phases: [
          {
            title: "Free intro call",
            pricePrefix: "",
            price: "Free",
            priceNote: "15 minutes",
            text: "A quick conversation to see whether custom AI is a fit for your team. No pitch deck, no obligation.",
            featuresTitle: "",
            features: [],
            note: "",
            cta: "Book a free 15-min call",
          },
          {
            title: "AI Operations Audit",
            pricePrefix: "",
            price: "$750",
            priceNote: "Fixed price",
            text: "We map your workflows, review your files and software, and deliver a custom AI blueprint with a fixed-price proposal for your build.",
            featuresTitle: "",
            features: [
              "Detailed process mapping",
              "Custom AI blueprint and an estimate of the time it could save you",
              "Fixed-price build proposal with scope, timeline and deliverables",
              "$750 credited in full if you move forward with your build",
            ],
            note: "",
            cta: "Book a free 15-min call",
          },
          {
            title: "Custom Build",
            pricePrefix: "Typically",
            price: "$5,000 to $15,000",
            priceNote: "One-time, fixed price after your audit",
            text: "Custom workflows built on your data and connected to your existing tools, tested against your real files, with your team trained to use them.",
            featuresTitle: "What affects the price",
            features: [
              "Number of workflows (quotes, reports, file search)",
              "Number of systems we connect to (CRM, Drive, Excel, databases)",
              "Condition of your data",
              "Complexity of the outputs",
              "Team size to train",
            ],
            note: "Smaller, single-workflow projects can start around $3,500.",
            cta: "Book a free 15-min call",
          },
          {
            title: "Managed Support",
            pricePrefix: "From",
            price: "$300/month",
            priceNote: "Optional",
            text: "Ongoing data syncs, maintenance and fixes as your business changes. Not every business needs it. Pricing depends on the number of workflows and how often your data or tools change.",
            featuresTitle: "",
            features: [
              "Monitoring and bug fixes",
              "Data sync upkeep",
              "Small improvements as your business changes",
              "A clear monthly scope, so you know what's included",
            ],
            note: "",
            cta: "Book a free 15-min call",
          },
        ],
        finePrint: [
          "Your build price is fixed once you accept the proposal. Anything outside the agreed scope is quoted separately before work begins.",
          "Third-party costs such as AI usage and software subscriptions are itemized in your proposal.",
          "Timelines depend on how quickly we receive file access and feedback.",
        ],
        audience: "Built for teams that spend hours each week on quotes, reports and searching for files.",
      },
      finalCta: {
        title: "Find out what custom AI can take off your plate.",
        titleAccent: "take off your plate.",
        text: "Start with a free 15-minute call. We'll look at your current software and show you where manual work could be reduced. If it's a fit, the next step is a $750 audit, credited toward your build.",
      },
    },
    comparisons: {
      fairnessNote:
        "ChatGPT and Gemini are great for drafting and brainstorming. Keep using them for that. Kranbos handles repeatable, data-dependent work that chat tools aren't built for.",
      trainingNote:
        "Instead of files copied into each employee's chat history, Kranbos keeps your data in one place with role-based access and logs.",
      hubEyebrow: "Why Kranbos",
      hubTitle: "How does Kranbos compare?",
      hubSubtitle:
        "See why off-the-shelf AI tools fall short for day-to-day small business operations.",
      readMore: "Read comparison",
      otherTitle: "More comparisons",
      footerTitle: "Comparisons",
      eyebrow: "Comparison",
      seeDifference: "See the difference",
      summaryTitle: "The short answer",
      scenarioEyebrow: "Same question, two answers",
      askedBy: "Your technician asks",
      kranbosLabel: "Kranbos Agent",
      tableTitle: "Side by side",
      tableFeature: "What matters",
      tableKranbos: "Kranbos",
      reasonsTitle: "Why it matters for your business",
      faqTitle: "Common questions",
      ctaEyebrow: "Next step",
      backHome: "Back to home",
      exampleNote: "Illustrative example with sample data.",
      disclaimer:
        "ChatGPT is a trademark of OpenAI. Gemini and Google Workspace are trademarks of Google LLC. Kranbos is independent and not affiliated with or endorsed by either company.",
      pages: {
        genericAi: {
          path: "/vs-generic-ai",
          navLabel: "vs. Generic AI Models",
          cardTitle: "Generic AI Models vs. Kranbos",
          cardText: "Why general-purpose AI gives broad answers instead of your exact business data.",
          metaTitle: "Generic AI vs. Kranbos | Custom AI Agents for Small Businesses",
          metaDescription:
            "Generic AI doesn't know your prices, contracts, or business history. See how Kranbos answers with your own documents and software, citing sources without guessing.",
          title: "Generic AI knows the internet. Kranbos knows your business.",
          subtitle:
            "General AI models learn from broad public information. They haven't seen your price sheet, your contracts, or last month's report, so they fill the gaps with confident guesses. Kranbos is built for teams that quote, report and answer the same questions all day, using your own files.",
          competitorName: "Generic AI",
          summaryCompetitorTitle: "Generic AI is fine for",
          summaryCompetitorItems: [
            "Drafting a general email or social post",
            "Explaining an industry concept",
            "Brainstorming marketing ideas",
          ],
          summaryKranbosTitle: "Kranbos is built for",
          summaryKranbosItems: [
            "Answers based on your own documents and records",
            "Live data from the tools your team already uses",
            "Answers that link to their source",
          ],
          scenarioQuestion: "What's our warranty on a commercial rooftop unit we installed in 2024?",
          competitorAnswer:
            "Commercial rooftop units typically carry a 1-year labor warranty and a 5 to 10-year parts warranty, depending on the manufacturer. Check your paperwork for details.",
          competitorNote: "Sounds reasonable, but it's an industry average, not your contract.",
          kranbosAnswer: "Per section 4.2 of your 2024 Commercial Service Agreement:",
          kranbosBullets: [
            "Parts & labor: 2-year full coverage",
            "Compressor: 5-year limited warranty",
          ],
          kranbosSource: "Google Drive › Contracts › 2024 Commercial Service Agreement.pdf",
            rows: [
              {
                label: "Data source",
                competitor: "Someone pastes or uploads files each time",
                kranbos: "Connected to your price sheets, CRM, and documents, and kept in sync",
              },
              {
                label: "Output",
                competitor: "Chat text you copy and reformat",
                kranbos: "Finished PDFs, spreadsheets, and CRM records",
              },
              {
                label: "Workflow",
                competitor: "Someone has to start each task",
                kranbos: "Runs on its own from a form or CRM event",
              },
              {
                label: "Control",
                competitor: "Each employee decides what to paste in",
                kranbos: "One governed place with role-based access and activity logs",
              },
            ],
          reasons: [
            {
              icon: "gpp_maybe",
              title: "Wrong answers cost real money",
              text: "A made-up warranty term or price quoted on site turns into a refund, a callback, or an unhappy customer.",
            },
            {
              icon: "folder_managed",
              title: "Your knowledge is already written down",
              text: "Contracts, price sheets, manuals, and job notes hold the answers. Kranbos makes them easy to find.",
            },
            {
              icon: "verified",
              title: "Trust you can check",
              text: "Answers link to their source, so your team can check them in one tap instead of calling the office.",
            },
          ],
          faqs: [
            {
              q: "Can't I just upload my documents to a generic AI tool?",
              a: "You can for one-off questions, but someone has to keep uploading the latest versions, and each person gets different results. Kranbos keeps your documents and software connected and current for the whole team.",
            },
            {
              q: "Is my business data used to train public AI models?",
              a: "No. Your agent is set up for your business only, and your documents and records are not used to train public models.",
            },
            {
              q: "Do my technicians need to learn a new app?",
              a: "No. We deliver the agent where your team already works, like text, chat, or email, so there's nothing new to learn.",
            },
          ],
          ctaTitle: "Get answers from your business, not the internet",
          ctaText:
            "Start with a free 15-minute call. We'll look at where your team loses time and whether a custom agent is a fit.",
        },
        chatgpt: {
          path: "/vs-chatgpt",
          navLabel: "vs. ChatGPT",
          cardTitle: "ChatGPT vs. Kranbos",
          cardText: "Why copy-pasting into ChatGPT falls short for real daily team workflows.",
          metaTitle: "ChatGPT vs. Kranbos | AI Agents Connected to Your Business Tools",
          metaDescription:
            "ChatGPT helps with one-off tasks. Kranbos connects AI to your business files and software to automate everyday work without constant copying and pasting.",
          title: "ChatGPT is a great assistant. It isn't connected to your business.",
          subtitle:
            "To get a useful answer from ChatGPT, someone has to find the right data, copy it out of your software, paste it in, and double-check the result. Every day. For teams that quote, report and answer the same questions all day, Kranbos connects to your tools directly, so the answers are already there.",
          competitorName: "ChatGPT",
          summaryCompetitorTitle: "ChatGPT is great for",
          summaryCompetitorItems: [
            "Writing and polishing one-off emails",
            "Summarizing a document you paste in",
            "Individual research and brainstorming",
          ],
          summaryKranbosTitle: "Kranbos is built for",
          summaryKranbosItems: [
            "Daily team operations, not one-off chats",
            "Live connections to your job, accounting & file tools",
            "Consistent answers for everyone on the team",
          ],
          scenarioQuestion: "Which jobs finished last week still haven't been invoiced?",
          competitorAnswer:
            "I don't have access to your job management or accounting systems. If you export last week's completed jobs and your invoice list and paste them here, I can compare them for you.",
          competitorNote:
            "Now someone has to export two reports, paste them in, and check the result.",
          kranbosAnswer: "3 completed jobs from last week have no invoice in QuickBooks yet:",
          kranbosBullets: [
            "#4471 Water heater replacement (Rivera): $2,380",
            "#4475 Panel upgrade (Oak St. Dental): $4,150",
            "#4482 Drain repair (Chen): $640",
          ],
          kranbosSource: "Jobber (completed jobs) + QuickBooks (invoices)",
            rows: [
              {
                label: "Data source",
                competitor: "Someone pastes or uploads files each time",
                kranbos: "Connected to your price sheets, CRM, and documents, and kept in sync",
              },
              {
                label: "Output",
                competitor: "Chat text you copy and reformat",
                kranbos: "Finished PDFs, spreadsheets, and CRM records",
              },
              {
                label: "Workflow",
                competitor: "Someone has to start each task",
                kranbos: "Runs on its own from a form or CRM event",
              },
              {
                label: "Control",
                competitor: "Each employee decides what to paste in",
                kranbos: "One governed place with role-based access and activity logs",
              },
            ],
          reasons: [
            {
              icon: "content_paste_off",
              title: "Copy-paste is the admin work",
              text: "If your office manager spends an hour prepping data for ChatGPT, you haven't saved the hour. You've moved it.",
            },
            {
              icon: "groups",
              title: "Your team needs the same answer",
              text: "Ten people asking in ten different ways get ten different results. Kranbos gives everyone the same source of truth.",
            },
            {
              icon: "schedule_send",
              title: "The best work happens without asking",
              text: "Unbilled-job alerts and Monday owner reports show up on their own. No one has to remember to open a chat.",
            },
          ],
          faqs: [
            {
              q: "We already pay for ChatGPT. Why add Kranbos?",
              a: "Keep using ChatGPT for writing and brainstorming. Kranbos handles the operational side: answering from your own records, connecting your tools, and running reports automatically.",
            },
            {
              q: "Can't ChatGPT connect to my apps?",
              a: "Some connections exist, but setting them up for your exact tools, permissions, and workflows, and keeping them working, is left to you. Kranbos builds and maintains that for you.",
            },
            {
              q: "Does my team have to change how they work?",
              a: "No. The agent works alongside the software and channels your team already uses.",
            },
          ],
          ctaTitle: "Stop copy-pasting. Start getting answers.",
          ctaText:
            "Book a free 15-minute call and we'll show you which daily tasks could run on their own, using the tools you already have.",
        },
        gemini: {
          path: "/vs-gemini",
          navLabel: "vs. Gemini",
          cardTitle: "Gemini vs. Kranbos",
          cardText: "How Kranbos adapts to the software you already use instead of fitting you into Google templates.",
          metaTitle: "Gemini vs. Kranbos | Custom AI for Growing Businesses",
          metaDescription:
            "Gemini works inside Google Workspace. Kranbos builds custom AI workflows connected to the files and software your business already uses.",
          title: "Gemini fits Google's way of working. Kranbos fits yours.",
          subtitle:
            "Gemini shines inside Gmail, Docs, and Sheets. But your quotes, contracts and reports don't run only on Google. Your price sheets sit in Excel, your deals in your CRM, and your numbers in QuickBooks. Kranbos builds around all of it.",
          competitorName: "Gemini",
          summaryCompetitorTitle: "Gemini is great for",
          summaryCompetitorItems: [
            "Drafting in Gmail and Google Docs",
            "Summarizing files stored in Google Drive",
            "Teams that run almost entirely on Google Workspace",
          ],
          summaryKranbosTitle: "Kranbos is built for",
          summaryKranbosItems: [
            "Businesses that run on field service & accounting software",
            "Custom workflows designed around how your team works",
            "Connecting Google tools and everything else you use",
          ],
          scenarioQuestion: "Give me this week's summary for the Monday meeting.",
          competitorAnswer:
            "Here's a summary of the files and emails I can see in your Google Workspace. I don't have access to job or invoice data stored outside Google.",
          competitorNote: "Your jobs and invoices live in other tools, so the summary is incomplete.",
          kranbosAnswer: "Here's your week at a glance (Mon–Fri):",
          kranbosBullets: [
            "42 jobs completed, 5 callbacks (down 12% vs. last week)",
            "Invoiced: $61,300 · Outstanding over 30 days: $8,900",
            "Top crew by completed jobs: Team B (14)",
          ],
          kranbosSource: "ServiceTitan + QuickBooks + Google Drive",
            rows: [
              {
                label: "Data source",
                competitor: "What it can see in Google apps; everything else gets pasted or uploaded",
                kranbos: "Connected to your price sheets, CRM, and documents, and kept in sync",
              },
              {
                label: "Output",
                competitor: "Chat text and drafts inside Google apps",
                kranbos: "Finished PDFs, spreadsheets, and CRM records",
              },
              {
                label: "Workflow",
                competitor: "Someone has to start each task",
                kranbos: "Runs on its own from a form or CRM event",
              },
              {
                label: "Control",
                competitor: "Each employee decides what to share with it",
                kranbos: "One governed place with role-based access and activity logs",
              },
            ],
          reasons: [
            {
              icon: "hub",
              title: "Your business runs on more than one platform",
              text: "Field apps, accounting, supplier portals, email, and PDFs. An agent that only sees one ecosystem only sees part of the picture.",
            },
            {
              icon: "tune",
              title: "Your process is your advantage",
              text: "You've refined how you dispatch, quote, and follow up. Kranbos builds around that instead of asking you to adopt someone else's template.",
            },
            {
              icon: "do_not_disturb_on",
              title: "No migration project",
              text: "You don't need to move files, switch email providers, or retrain your team. Kranbos plugs into what's already working.",
            },
          ],
          faqs: [
            {
              q: "We use Google Workspace. Does Kranbos work with it?",
              a: "Yes. Kranbos connects to Google Drive, Gmail, and Sheets alongside your field service and accounting software, so you get the full picture in one place.",
            },
            {
              q: "Should we stop using Gemini?",
              a: "Not at all. Gemini is useful for writing inside Google apps. Kranbos covers the operational workflows that span all of your tools.",
            },
            {
              q: "How long does it take to set up?",
              a: "Smaller builds are typically built, tested and handed over within 2 to 3 weeks. Larger, multi-workflow projects are scheduled in your proposal.",
            },
          ],
          ctaTitle: "Get an AI agent that fits the way you already work",
          ctaText:
            "Book a free 15-minute call. We'll look at your tools and workflows and whether a custom agent is a fit.",
        },
      },
    },
  },
  es: {
    aiAudit: {
      cta: "Agenda tu Auditoría de Operaciones con IA ($750)",
      modalTitle: "Agenda tu Auditoría de Operaciones con IA",
      modalSubtitle:
        "Cuéntanos un poco sobre tu negocio. Te responderemos en un día hábil para agendar tu auditoría.",
      nameLabel: "Tu nombre",
      businessLabel: "Nombre del negocio",
      emailLabel: "Correo electrónico",
      phoneLabel: "Teléfono",
      tradeLabel: "Industria",
      tradeOptions: [
        "Construcción y Manufactura",
        "Servicios Legales",
        "Salud y Medicina",
        "Retail y E-Commerce",
        "Bienes Raíces y Administración de Propiedades",
        "Servicios Locales y Belleza",
        "Otra",
      ],
      teamSizeLabel: "Tamaño del equipo",
      teamSizeOptions: ["1–9 personas", "10–20 personas", "21–50 personas", "Más de 50 personas"],
      toolsLabel: "Herramientas que usas hoy",
      toolsPlaceholder: "ej. QuickBooks, HubSpot, Google Drive",
      headacheLabel: "Tu mayor dolor de cabeza administrativo",
      headachePlaceholder:
        "ej. Sacar los números semanales le lleva todo el lunes a mi encargada de oficina.",
      selectPlaceholder: "Seleccionar…",
      submit: "Solicitar una llamada",
      cancel: "Cancelar",
      close: "Cerrar",
      priceNote: "La auditoría cuesta $750 y se acredita por completo a tu desarrollo si avanzas. Sin compromiso de trabajo adicional.",
      callCta: "Agenda una llamada gratis de 15 min",
      callNote: "La llamada es gratis. Si encajamos, la auditoría cuesta $750 y se acredita a tu desarrollo.",
      sentTitle: "Gracias, recibimos tu solicitud",
      sentText:
        "Te responderemos en un día hábil para agendar tu auditoría.",
      callSentText: "Te responderemos en un día hábil para agendar tu llamada gratis.",
      done: "Listo",
    },
    aiSimulator: {
      windowTitle: "Vista previa del Agente Kranbos",
      suggestionsLabel: "Prueba una pregunta de ejemplo:",
      placeholder: "Pregunta sobre presupuestos, especificaciones o facturas…",
      send: "Enviar",
      typing: "Revisando tus archivos y herramientas…",
      userLabel: "Tú",
      agentLabel: "Agente Kranbos",
      sourceLabel: "Fuente",
      exampleNote: "Datos de negocio de ejemplo con fines ilustrativos.",
      fallback:
        "Esta vista previa solo responde las preguntas de ejemplo de arriba. En tu negocio, el agente responde con tus propios documentos, listas de precios y software, y muestra de dónde sale cada respuesta.",
      scenarios: [
        {
          id: "warranty",
          chip: "Consulta de garantía",
          question:
            "¿Cuál es la garantía de las instalaciones comerciales de HVAC en nuestro contrato de servicio 2024?",
          answer: "Según la sección 4.2 de tu Contrato de Servicio Comercial 2024:",
          bullets: [
            "Piezas y mano de obra: cobertura total por 2 años",
            "Compresor: garantía limitada de 5 años",
            "Requiere la visita de mantenimiento anual registrada",
          ],
          followUp: "",
          source: "Google Drive › Contratos › Contrato de Servicio Comercial 2024.pdf",
          keywords: ["garantía", "garantia", "cobertura"],
        },
        {
          id: "unbilled",
          chip: "Trabajos sin facturar",
          question: "¿Qué trabajos terminados la semana pasada todavía no se facturaron?",
          answer: "3 trabajos completados la semana pasada aún no tienen factura en QuickBooks:",
          bullets: [
            "#4471 Reemplazo de calentador de agua (Rivera): $2,380",
            "#4475 Actualización de tablero (Oak St. Dental): $4,150",
            "#4482 Reparación de desagüe (Chen): $640",
          ],
          followUp: "Total sin facturar: $7,170. ¿Quieres que prepare las facturas?",
          source: "Jobber (trabajos completados) + QuickBooks (facturas)",
          keywords: ["factura", "facturar", "facturado", "facturaron", "cobrar"],
        },
        {
          id: "report",
          chip: "Reporte semanal",
          question: "Dame el resumen de esta semana para la reunión del lunes.",
          answer: "Así fue tu semana (lun–vie):",
          bullets: [
            "42 trabajos completados, 5 revisitas (12% menos que la semana pasada)",
            "Facturado: $61,300 · Pendiente a más de 30 días: $8,900",
            "Mejor equipo por trabajos completados: Equipo B (14)",
          ],
          followUp: "El reporte completo llega a tu correo cada lunes a las 7:00 AM.",
          source: "ServiceTitan + QuickBooks",
          keywords: ["reporte", "resumen", "semana", "semanal", "números", "reunión"],
        },
        {
          id: "pricing",
          chip: "Lista de precios",
          question: "¿Cuánto cobramos por cambiar un calentador de 50 galones con retiro del viejo?",
          answer: "Según tu Lista de Precios Residencial 2024:",
          bullets: [
            "Calentador a gas de 50 gal, instalado: $1,850",
            "Retiro y desecho: $95",
            "Permiso (si aplica): se cobra al costo",
          ],
          followUp: "Los miembros del plan de mantenimiento tienen 10% de descuento en mano de obra.",
          source: "Google Drive › Precios › Lista de Precios Residencial 2024.xlsx",
          keywords: ["precio", "precios", "cobramos", "costo", "presupuesto", "calentador"],
        },
      ],
    },
    aiHome: {
      hero: {
        beforeTitle: "Trabajo manual y caos",
        beforeAlt:
          "Un dueño de negocio estresado en un escritorio desordenado con hojas de cálculo caóticas, papeles sueltos y notas adhesivas",
        afterTitle: "Flujo de trabajo automatizado",
        afterAlt:
          "El mismo dueño de negocio relajado con un café, mirando un panel limpio de Kranbos con cotizaciones generadas y tareas automatizadas completadas",
        title: "Para negocios que cotizan, reportan y responden las mismas preguntas todo el día",
        titleAccent: "las mismas preguntas todo el día",
        badge: "Basta de copiar y pegar cotizaciones y reportes",
        subtitle:
          "IA a medida conectada a tus listas de precios, archivos y software. Prepara cotizaciones, reportes y respuestas. Tu equipo revisa y envía.",
      },
      industries: {
        title: "Pensado para el trabajo que tu equipo repite todos los días.",
        titleAccent: "el trabajo que tu equipo repite",
        subtitle: "Tres tipos de trabajo que automatizamos, con ejemplos de proyectos reales de clientes.",
        groups: [
          {
            id: "quote-heavy",
            icon: "request_quote",
            title: "Negocios que cotizan mucho",
            who: "Oficios, construcción, manufactura, instaladores, paisajismo, eventos e imprentas.",
            examples: [
              "Cotizaciones PDF desde tus listas de precios",
              "Del formulario de ingreso a la cotización",
              "Presupuestos sincronizados con tu CRM",
            ],
          },
          {
            id: "document-heavy",
            icon: "description",
            title: "Negocios con mucho papeleo",
            who: "Estudios jurídicos, administración de propiedades, agencias de seguros, contadores y otros estudios profesionales.",
            examples: [
              "Búsqueda privada en tus contratos y procedimientos",
              "Respuestas desde tus propios archivos, con enlace a la fuente",
            ],
          },
          {
            id: "reporting-heavy",
            icon: "monitoring",
            title: "Negocios que viven de reportes",
            who: "Cualquier equipo que rehace el mismo reporte mensual desde hojas de cálculo.",
            examples: ["Reportes ejecutivos automáticos desde tus datos de proyectos", "Paneles que se actualizan de forma programada"],
          },
        ],
        tilesTitle: "Industrias para las que podemos construir",
        catchAll:
          "¿No ves tu industria? Si tu equipo trabaja con listas de precios, contratos u hojas de cálculo, probablemente podamos ayudarte.",
        catchAllCta: "Agenda una llamada gratis de 15 min.",
        items: [
          { icon: "construction", label: "Construcción, Oficios y Manufactura", path: "/industries/construction-manufacturing/" },
          { icon: "apartment", label: "Bienes Raíces y Administración de Propiedades", path: "/industries/real-estate/" },
          { icon: "storefront", label: "Retail y E-Commerce", path: "/industries/retail-ecommerce/" },
          { icon: "spa", label: "Servicios Locales y Belleza", path: "/industries/local-services/" },
          { icon: "gavel", label: "Estudios Jurídicos y Servicios Legales", path: "/industries/law-firms/" },
          { icon: "medical_services", label: "Consultorios Médicos y de Salud", path: "/industries/medical-practices/" },
        ],
      },
      value: {
        title:
          "La IA genérica te da respuestas genéricas. Kranbos crea IA que conoce tu negocio.",
        eyebrow: "Por qué Kranbos",
        titleAccent: "conoce tu negocio.",
        subtitle:
          "ChatGPT y Gemini son excelentes para redactar y generar ideas. Sigue usándolos para eso.\nKranbos se encarga del trabajo repetitivo que depende de tus datos, para el que los chats de IA no están hechos.",
        featureHeader: "Característica",
        genericHeader: "Chats de IA (como ChatGPT o Gemini)",
        kranbosHeader: "Kranbos",
          rows: [
            {
              feature: "Origen de los datos",
              generic: "Alguien pega o sube archivos cada vez",
              kranbos: "Conectado a tus listas de precios, CRM y documentos, y sincronizado",
            },
            {
              feature: "Resultado",
              generic: "Texto de chat que hay que copiar y dar formato",
              kranbos: "PDFs, hojas de cálculo y registros de CRM terminados",
            },
            {
              feature: "Flujo de trabajo",
              generic: "Alguien tiene que iniciar cada tarea",
              kranbos: "Se activa solo desde un formulario o un evento del CRM",
            },
            {
              feature: "Control",
              generic: "Cada empleado decide qué pegar",
              kranbos: "Un solo lugar controlado, con acceso por roles y registro de actividad",
            },
          ],
        privacyNote:
          "En vez de archivos copiados en el historial de chat de cada empleado, Kranbos mantiene tus datos en un solo lugar, con acceso por roles y registros.",
      },
      howItWorks: {
        title: "De la primera llamada a tu IA funcionando en 4 pasos",
        eyebrow: "Cómo funciona",
        titleAccent: "en 4 pasos",
        stepLabel: "Paso",
        steps: [
          {
            icon: "call",
            title: "Llamada gratis de 15 min",
            text: "Cuéntanos qué le quita tiempo a tu equipo. Te diremos con honestidad si la IA a medida encaja.",
          },
          {
            icon: "fact_check",
            title: "Auditoría de Operaciones con IA",
            text: "Mapeamos tus flujos y archivos, y te enviamos una propuesta a precio fijo. $750, acreditados a tu desarrollo.",
          },
          {
            icon: "build",
            title: "Desarrollo y pruebas",
            text: "Conectamos tus herramientas y probamos con tus archivos reales. Las cotizaciones y reportes pueden requerir aprobación antes de enviarse.",
          },
          {
            icon: "groups",
            title: "Capacitación y entrega",
            text: "Capacitamos a tu equipo con instrucciones simples. El soporte continuo es opcional.",
          },
        ],
      },
      security: {
        title: "Tus datos se mantienen privados y con control de acceso",
        eyebrow: "Seguridad y privacidad",
        titleAccent: "con control de acceso",
        trustLink: "Cómo manejamos tus datos",
        items: [
          {
            icon: "block",
            title: "Sin entrenar modelos públicos",
            text: "Tus archivos y listas de precios no se usan para entrenar modelos de IA públicos.",
          },
          {
            icon: "cloud_lock",
            title: "Nube privada aislada",
            text: "Tus datos viven en un entorno aislado y cifrado, dedicado a tu empresa.",
          },
          {
            icon: "admin_panel_settings",
            title: "Accesos por rol",
            text: "Los accesos se asignan por rol, así decides quién de tu equipo ve cada dato.",
          },
        ],
      },
      faq: {
        title: "Preguntas frecuentes",
        eyebrow: "Preguntas frecuentes",
        titleAccent: "Preguntas",
        items: [
          {
            q: "¿Mi equipo tendrá que aprender herramientas técnicas complicadas?",
            a: "No. Si tu equipo puede completar un formulario web, puede usar Kranbos. Nosotros nos encargamos de la configuración técnica.",
          },
          {
            q: "¿Y si nuestras hojas de cálculo o archivos están desordenados?",
            a: "Es normal. Durante tu auditoría limpiamos y estructuramos tus datos, probamos la IA con tus propias cotizaciones o reportes anteriores y te mostramos el porcentaje de coincidencia antes de lanzar. Lo que no tenga suficiente certeza se marca para revisión.",
          },
          {
            q: "¿Podemos revisar cotizaciones y reportes antes de enviarlos?",
            a: "Sí. Las cotizaciones y reportes pueden requerir aprobación antes de enviarse, hasta que confíes en los resultados.",
          },
          {
            q: "¿Tenemos que reemplazar el software que ya usamos?",
            a: "No. Kranbos se conecta a las herramientas que ya usas, como HubSpot, Jobber, Excel y Google Workspace.",
          },
          {
            q: "¿Cuánto tarda el proceso de implementación?",
            a: "Los desarrollos más pequeños suelen quedar construidos, probados y entregados en 2 a 3 semanas. Los proyectos más grandes, con varios flujos de trabajo, se programan en tu propuesta.",
          },
          {
            q: "¿Cuánto cuesta?",
            a: "La primera llamada de 15 minutos es gratis. La auditoría tiene un precio fijo de $750, acreditado a tu desarrollo. El desarrollo se cotiza después de la auditoría, normalmente entre $5,000 y $15,000, y los proyectos más pequeños desde unos $3,500. El soporte continuo es opcional y empieza en $300/mes.",
          },
          {
            q: "¿Necesito soporte continuo?",
            a: "No siempre. Algunos negocios funcionan bien por su cuenta después de la entrega. Te recomendaremos si el soporte tiene sentido cuando veamos cómo cambian tus flujos y datos con el tiempo.",
          },
        ],
      },
      engagement: {
        title: "Precios claros, adaptados a tu negocio",
        eyebrow: "Precios",
        titleAccent: "adaptados a tu negocio",
        subtitle:
          "Cada negocio funciona distinto, por eso cotizamos el desarrollo después de tu auditoría. Empieza con una llamada gratis, sigue con una auditoría y una propuesta de precio fijo, y luego decide.",
        featured: "Servicio principal",
        phases: [
          {
            title: "Llamada introductoria gratuita",
            pricePrefix: "",
            price: "Gratis",
            priceNote: "15 minutos",
            text: "Una conversación breve para ver si la IA a medida encaja con tu equipo. Sin presentaciones de venta, sin compromiso.",
            featuresTitle: "",
            features: [],
            note: "",
            cta: "Agenda una llamada gratis de 15 min",
          },
          {
            title: "Auditoría de Operaciones con IA",
            pricePrefix: "",
            price: "$750",
            priceNote: "Precio fijo",
            text: "Mapeamos tus flujos de trabajo, revisamos tus archivos y software, y te entregamos un plano de IA a medida con una propuesta de precio fijo para tu desarrollo.",
            featuresTitle: "",
            features: [
              "Mapeo detallado de procesos",
              "Plano de IA a medida y una estimación del tiempo que podría ahorrarte",
              "Propuesta de desarrollo a precio fijo con alcance, plazos y entregables",
              "$750 acreditados por completo si avanzas con tu desarrollo",
            ],
            note: "",
            cta: "Agenda una llamada gratis de 15 min",
          },
          {
            title: "Desarrollo a Medida",
            pricePrefix: "Normalmente",
            price: "$5,000 a $15,000",
            priceNote: "Pago único, precio fijo después de tu auditoría",
            text: "Flujos a medida construidos con tus datos y conectados a tus herramientas actuales, probados con tus archivos reales, y con tu equipo capacitado para usarlos.",
            featuresTitle: "Qué influye en el precio",
            features: [
              "Cantidad de flujos (cotizaciones, reportes, búsqueda de archivos)",
              "Cantidad de sistemas que conectamos (CRM, Drive, Excel, bases de datos)",
              "Estado de tus datos",
              "Complejidad de los resultados",
              "Tamaño del equipo a capacitar",
            ],
            note: "Los proyectos más pequeños, de un solo flujo, pueden empezar desde unos $3,500.",
            cta: "Agenda una llamada gratis de 15 min",
          },
          {
            title: "Soporte Gestionado",
            pricePrefix: "Desde",
            price: "$300/mes",
            priceNote: "Opcional",
            text: "Sincronización de datos, mantenimiento y correcciones continuas a medida que tu negocio cambia. No todos los negocios lo necesitan. El precio depende de la cantidad de flujos y de qué tan seguido cambian tus datos o herramientas.",
            featuresTitle: "",
            features: [
              "Monitoreo y corrección de errores",
              "Mantenimiento de la sincronización de datos",
              "Pequeñas mejoras a medida que tu negocio cambia",
              "Un alcance mensual claro, para que sepas qué está incluido",
            ],
            note: "",
            cta: "Agenda una llamada gratis de 15 min",
          },
        ],
        finePrint: [
          "El precio de tu desarrollo queda fijo cuando aceptas la propuesta. Todo lo que quede fuera del alcance acordado se cotiza por separado antes de empezar.",
          "Los costos de terceros, como el uso de IA y las suscripciones de software, se detallan en tu propuesta.",
          "Los plazos dependen de qué tan rápido recibimos acceso a los archivos y tus comentarios.",
        ],
        audience: "Hecho para equipos que pasan horas cada semana en cotizaciones, reportes y buscando archivos.",
      },
      finalCta: {
        title: "Descubre qué tareas puede quitarte de encima la IA a medida.",
        titleAccent: "quitarte de encima",
        text: "Empieza con una llamada gratis de 15 minutos. Revisaremos tu software actual y te mostraremos dónde se podría reducir el trabajo manual. Si encaja, el siguiente paso es una auditoría de $750, acreditada a tu desarrollo.",
      },
    },
    comparisons: {
      fairnessNote:
        "ChatGPT y Gemini son excelentes para redactar y generar ideas. Sigue usándolos para eso. Kranbos se encarga del trabajo repetitivo que depende de tus datos, para el que los chats de IA no están hechos.",
      trainingNote:
        "En vez de archivos copiados en el historial de chat de cada empleado, Kranbos mantiene tus datos en un solo lugar, con acceso por roles y registros.",
      hubEyebrow: "Por qué Kranbos",
      hubTitle: "¿Cómo se compara Kranbos?",
      hubSubtitle:
        "Descubre por qué las herramientas de IA genéricas se quedan cortas para las operaciones diarias de un pequeño negocio.",
      readMore: "Ver comparación",
      otherTitle: "Más comparaciones",
      footerTitle: "Comparaciones",
      eyebrow: "Comparación",
      seeDifference: "Ver la diferencia",
      summaryTitle: "La respuesta corta",
      scenarioEyebrow: "La misma pregunta, dos respuestas",
      askedBy: "Tu técnico pregunta",
      kranbosLabel: "Agente Kranbos",
      tableTitle: "Lado a lado",
      tableFeature: "Lo que importa",
      tableKranbos: "Kranbos",
      reasonsTitle: "Por qué importa para tu negocio",
      faqTitle: "Preguntas frecuentes",
      ctaEyebrow: "Siguiente paso",
      backHome: "Volver al inicio",
      exampleNote: "Ejemplo ilustrativo con datos de muestra.",
      disclaimer:
        "ChatGPT es una marca registrada de OpenAI. Gemini y Google Workspace son marcas registradas de Google LLC. Kranbos es independiente y no está afiliado ni respaldado por ninguna de esas empresas.",
      pages: {
        genericAi: {
          path: "/vs-generic-ai",
          navLabel: "vs. Modelos de IA Genéricos",
          cardTitle: "Modelos de IA Genéricos vs. Kranbos",
          cardText: "Por qué la IA genérica da respuestas amplias en lugar de los datos exactos de tu negocio.",
          metaTitle: "IA Genérica vs. Kranbos | Agentes de IA a Medida para Pequeños Negocios",
          metaDescription:
            "Los modelos de IA genéricos no conocen tus precios, contratos ni historial. Descubre cómo Kranbos responde con tus propios documentos y herramientas.",
          title: "La IA genérica conoce internet. Kranbos conoce tu negocio.",
          subtitle:
            "Los modelos de IA generales aprenden de información pública amplia. No vieron tu lista de precios, tus contratos ni el reporte del mes pasado, así que llenan los huecos con suposiciones muy seguras. Kranbos está hecho para equipos que cotizan, reportan y responden las mismas preguntas todo el día, usando tus propios archivos.",
          competitorName: "IA genérica",
          summaryCompetitorTitle: "La IA genérica sirve para",
          summaryCompetitorItems: [
            "Redactar un correo o publicación general",
            "Explicar un concepto de la industria",
            "Generar ideas de marketing",
          ],
          summaryKranbosTitle: "Kranbos está hecho para",
          summaryKranbosItems: [
            "Respuestas basadas en tus propios documentos y registros",
            "Datos en vivo de las herramientas que ya usa tu equipo",
            "Respuestas con enlace a su fuente",
          ],
          scenarioQuestion:
            "¿Cuál es nuestra garantía para una unidad comercial de techo que instalamos en 2024?",
          competitorAnswer:
            "Las unidades comerciales de techo suelen tener 1 año de garantía de mano de obra y de 5 a 10 años en piezas, según el fabricante. Revisa tu documentación para más detalles.",
          competitorNote: "Suena razonable, pero es un promedio de la industria, no tu contrato.",
          kranbosAnswer: "Según la sección 4.2 de tu Contrato de Servicio Comercial 2024:",
          kranbosBullets: [
            "Piezas y mano de obra: cobertura total por 2 años",
            "Compresor: garantía limitada de 5 años",
          ],
          kranbosSource: "Google Drive › Contratos › Contrato de Servicio Comercial 2024.pdf",
            rows: [
              {
                label: "Origen de los datos",
                competitor: "Alguien pega o sube archivos cada vez",
                kranbos: "Conectado a tus listas de precios, CRM y documentos, y sincronizado",
              },
              {
                label: "Resultado",
                competitor: "Texto de chat que hay que copiar y dar formato",
                kranbos: "PDFs, hojas de cálculo y registros de CRM terminados",
              },
              {
                label: "Flujo de trabajo",
                competitor: "Alguien tiene que iniciar cada tarea",
                kranbos: "Se activa solo desde un formulario o un evento del CRM",
              },
              {
                label: "Control",
                competitor: "Cada empleado decide qué pegar",
                kranbos: "Un solo lugar controlado, con acceso por roles y registro de actividad",
              },
            ],
          reasons: [
            {
              icon: "gpp_maybe",
              title: "Las respuestas equivocadas cuestan dinero",
              text: "Una garantía o un precio inventado en el sitio se convierte en un reembolso, una revisita o un cliente molesto.",
            },
            {
              icon: "folder_managed",
              title: "Tu conocimiento ya está escrito",
              text: "Contratos, listas de precios, manuales y notas de trabajo tienen las respuestas. Kranbos las hace fáciles de encontrar.",
            },
            {
              icon: "verified",
              title: "Confianza que puedes verificar",
              text: "Las respuestas enlazan a su fuente, así tu equipo puede verificarlas con un toque en lugar de llamar a la oficina.",
            },
          ],
          faqs: [
            {
              q: "¿No puedo simplemente subir mis documentos a una IA genérica?",
              a: "Puedes para preguntas puntuales, pero alguien tiene que seguir subiendo las versiones nuevas, y cada persona obtiene resultados distintos. Kranbos mantiene tus documentos y software conectados y actualizados para todo el equipo.",
            },
            {
              q: "¿Los datos de mi negocio se usan para entrenar modelos públicos?",
              a: "No. Tu agente se configura solo para tu negocio, y tus documentos y registros no se usan para entrenar modelos públicos.",
            },
            {
              q: "¿Mis técnicos tienen que aprender una app nueva?",
              a: "No. Entregamos el agente donde tu equipo ya trabaja, como mensajes, chat o correo, así que no hay nada nuevo que aprender.",
            },
          ],
          ctaTitle: "Obtén respuestas de tu negocio, no de internet",
          ctaText:
            "Empieza con una llamada gratis de 15 minutos. Veremos dónde pierde tiempo tu equipo y si un agente a medida encaja.",
        },
        chatgpt: {
          path: "/vs-chatgpt",
          navLabel: "vs. ChatGPT",
          cardTitle: "ChatGPT vs. Kranbos",
          cardText: "Por qué copiar y pegar en ChatGPT no alcanza para el trabajo diario de un equipo.",
          metaTitle: "ChatGPT vs. Kranbos | Agentes de IA Conectados a tus Herramientas",
          metaDescription:
            "ChatGPT ayuda con tareas puntuales. Kranbos conecta la IA a tus archivos y software para automatizar el trabajo diario sin copiar y pegar constantemente.",
          title: "ChatGPT es un gran asistente. Pero no está conectado a tu negocio.",
          subtitle:
            "Para obtener una respuesta útil de ChatGPT, alguien tiene que encontrar los datos, copiarlos de tu software, pegarlos y revisar el resultado. Todos los días. Para equipos que cotizan, reportan y responden las mismas preguntas todo el día, Kranbos se conecta directamente a tus herramientas, así que las respuestas ya están ahí.",
          competitorName: "ChatGPT",
          summaryCompetitorTitle: "ChatGPT es excelente para",
          summaryCompetitorItems: [
            "Escribir y pulir correos puntuales",
            "Resumir un documento que pegas",
            "Investigar y generar ideas de forma individual",
          ],
          summaryKranbosTitle: "Kranbos está hecho para",
          summaryKranbosItems: [
            "Operaciones diarias del equipo, no chats sueltos",
            "Conexiones en vivo con tus herramientas de trabajos, contabilidad y archivos",
            "Respuestas consistentes para todo el equipo",
          ],
          scenarioQuestion: "¿Qué trabajos terminados la semana pasada todavía no se facturaron?",
          competitorAnswer:
            "No tengo acceso a tus sistemas de gestión de trabajos ni de contabilidad. Si exportas los trabajos completados de la semana pasada y tu lista de facturas y los pegas aquí, puedo compararlos.",
          competitorNote: "Ahora alguien tiene que exportar dos reportes, pegarlos y revisar el resultado.",
          kranbosAnswer: "3 trabajos completados la semana pasada aún no tienen factura en QuickBooks:",
          kranbosBullets: [
            "#4471 Reemplazo de calentador de agua (Rivera): $2,380",
            "#4475 Actualización de tablero (Oak St. Dental): $4,150",
            "#4482 Reparación de desagüe (Chen): $640",
          ],
          kranbosSource: "Jobber (trabajos completados) + QuickBooks (facturas)",
            rows: [
              {
                label: "Origen de los datos",
                competitor: "Alguien pega o sube archivos cada vez",
                kranbos: "Conectado a tus listas de precios, CRM y documentos, y sincronizado",
              },
              {
                label: "Resultado",
                competitor: "Texto de chat que hay que copiar y dar formato",
                kranbos: "PDFs, hojas de cálculo y registros de CRM terminados",
              },
              {
                label: "Flujo de trabajo",
                competitor: "Alguien tiene que iniciar cada tarea",
                kranbos: "Se activa solo desde un formulario o un evento del CRM",
              },
              {
                label: "Control",
                competitor: "Cada empleado decide qué pegar",
                kranbos: "Un solo lugar controlado, con acceso por roles y registro de actividad",
              },
            ],
          reasons: [
            {
              icon: "content_paste_off",
              title: "Copiar y pegar es el trabajo administrativo",
              text: "Si tu encargada de oficina pasa una hora preparando datos para ChatGPT, no ahorraste la hora. Solo la moviste.",
            },
            {
              icon: "groups",
              title: "Tu equipo necesita la misma respuesta",
              text: "Diez personas preguntando de diez formas distintas obtienen diez resultados distintos. Kranbos le da a todos la misma fuente de verdad.",
            },
            {
              icon: "schedule_send",
              title: "El mejor trabajo ocurre sin tener que pedirlo",
              text: "Las alertas de trabajos sin facturar y el reporte del lunes llegan solos. Nadie tiene que acordarse de abrir un chat.",
            },
          ],
          faqs: [
            {
              q: "Ya pagamos ChatGPT. ¿Por qué sumar Kranbos?",
              a: "Sigue usando ChatGPT para escribir e idear. Kranbos se ocupa del lado operativo: responder con tus propios registros, conectar tus herramientas y generar reportes automáticamente.",
            },
            {
              q: "¿ChatGPT no puede conectarse a mis apps?",
              a: "Existen algunas conexiones, pero configurarlas para tus herramientas, permisos y flujos exactos, y mantenerlas funcionando, queda a tu cargo. Kranbos lo construye y lo mantiene por ti.",
            },
            {
              q: "¿Mi equipo tiene que cambiar su forma de trabajar?",
              a: "No. El agente funciona junto al software y los canales que tu equipo ya usa.",
            },
          ],
          ctaTitle: "Deja de copiar y pegar. Empieza a obtener respuestas.",
          ctaText:
            "Agenda una llamada gratis de 15 minutos y te mostraremos qué tareas diarias podrían funcionar solas, con las herramientas que ya tienes.",
        },
        gemini: {
          path: "/vs-gemini",
          navLabel: "vs. Gemini",
          cardTitle: "Gemini vs. Kranbos",
          cardText: "Cómo Kranbos se adapta al software que ya usas en lugar de encajarte en plantillas de Google.",
          metaTitle: "Gemini vs. Kranbos | IA a Medida para Negocios en Crecimiento",
          metaDescription:
            "Gemini funciona dentro de Google Workspace. Kranbos crea flujos de IA a medida conectados a los archivos y software que tu negocio ya utiliza.",
          title: "Gemini se adapta a la forma de trabajar de Google. Kranbos, a la tuya.",
          subtitle:
            "Gemini brilla dentro de Gmail, Docs y Sheets. Pero tus cotizaciones, contratos y reportes no funcionan solo en Google. Tus listas de precios están en Excel, tus oportunidades en tu CRM y tus números en QuickBooks. Kranbos se construye alrededor de todo eso.",
          competitorName: "Gemini",
          summaryCompetitorTitle: "Gemini es excelente para",
          summaryCompetitorItems: [
            "Redactar en Gmail y Google Docs",
            "Resumir archivos guardados en Google Drive",
            "Equipos que trabajan casi todo en Google Workspace",
          ],
          summaryKranbosTitle: "Kranbos está hecho para",
          summaryKranbosItems: [
            "Negocios que funcionan con software de servicios de campo y contabilidad",
            "Flujos a medida diseñados según cómo trabaja tu equipo",
            "Conectar las herramientas de Google y todo lo demás que usas",
          ],
          scenarioQuestion: "Dame el resumen de esta semana para la reunión del lunes.",
          competitorAnswer:
            "Aquí tienes un resumen de los archivos y correos que puedo ver en tu Google Workspace. No tengo acceso a datos de trabajos o facturas guardados fuera de Google.",
          competitorNote: "Tus trabajos y facturas viven en otras herramientas, así que el resumen queda incompleto.",
          kranbosAnswer: "Así fue tu semana (lun–vie):",
          kranbosBullets: [
            "42 trabajos completados, 5 revisitas (12% menos que la semana pasada)",
            "Facturado: $61,300 · Pendiente a más de 30 días: $8,900",
            "Mejor equipo por trabajos completados: Equipo B (14)",
          ],
          kranbosSource: "ServiceTitan + QuickBooks + Google Drive",
            rows: [
              {
                label: "Origen de los datos",
                competitor: "Lo que ve en las apps de Google; el resto hay que pegarlo o subirlo",
                kranbos: "Conectado a tus listas de precios, CRM y documentos, y sincronizado",
              },
              {
                label: "Resultado",
                competitor: "Texto de chat y borradores dentro de las apps de Google",
                kranbos: "PDFs, hojas de cálculo y registros de CRM terminados",
              },
              {
                label: "Flujo de trabajo",
                competitor: "Alguien tiene que iniciar cada tarea",
                kranbos: "Se activa solo desde un formulario o un evento del CRM",
              },
              {
                label: "Control",
                competitor: "Cada empleado decide qué compartir",
                kranbos: "Un solo lugar controlado, con acceso por roles y registro de actividad",
              },
            ],
          reasons: [
            {
              icon: "hub",
              title: "Tu negocio funciona en más de una plataforma",
              text: "Apps de campo, contabilidad, portales de proveedores, correo y PDFs. Un agente que solo ve un ecosistema solo ve parte del panorama.",
            },
            {
              icon: "tune",
              title: "Tu proceso es tu ventaja",
              text: "Perfeccionaste cómo despachas, cotizas y das seguimiento. Kranbos se construye alrededor de eso en lugar de pedirte adoptar la plantilla de otro.",
            },
            {
              icon: "do_not_disturb_on",
              title: "Sin proyecto de migración",
              text: "No necesitas mover archivos, cambiar de proveedor de correo ni volver a capacitar a tu equipo. Kranbos se conecta a lo que ya funciona.",
            },
          ],
          faqs: [
            {
              q: "Usamos Google Workspace. ¿Kranbos funciona con eso?",
              a: "Sí. Kranbos se conecta a Google Drive, Gmail y Sheets junto con tu software de servicios de campo y contabilidad, para que tengas el panorama completo en un solo lugar.",
            },
            {
              q: "¿Deberíamos dejar de usar Gemini?",
              a: "Para nada. Gemini es útil para escribir dentro de las apps de Google. Kranbos cubre los flujos operativos que abarcan todas tus herramientas.",
            },
            {
              q: "¿Cuánto tarda la implementación?",
              a: "Los desarrollos más pequeños suelen quedar construidos, probados y entregados en 2 a 3 semanas. Los proyectos más grandes, con varios flujos de trabajo, se programan en tu propuesta.",
            },
          ],
          ctaTitle: "Obtén un agente de IA que se adapte a como ya trabajas",
          ctaText:
            "Agenda una llamada gratis de 15 minutos. Revisaremos tus herramientas y flujos, y si un agente a medida encaja con ellos.",
        },
      },
    },
  },
};
